import React, { useState, useRef, useEffect, useCallback, useContext, forwardRef, useImperativeHandle } from 'react'
import ReactDOM from 'react-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSquare, faCheckSquare } from '@fortawesome/free-regular-svg-icons'
import { faSpinner } from '@fortawesome/free-solid-svg-icons'
import { Modal as ReactModal } from "react-bootstrap"
import AppContext from '../contexts/AppContext'
import useToastContext from "../hooks/useToastContext";

const Checkbox = ({
  label,
  children,
  active,
  onChange,
  disabled,
  checkedIcon,
  uncheckedIcon
}) => {
    return (
        <div
            className={
                'my-1 ps-1' + (active ? ' bg-light-blue' : '') + (disabled ? ' text-muted' : ' cursor-pointer')
            }
            onClick={() => {
                if(typeof onChange == 'function' && !disabled){
                    onChange(!active)
                }
            }}
        >
            <span className={!active ? 'text-muted' : ''}>
                {active && <FontAwesomeIcon icon={checkedIcon ? checkedIcon : faCheckSquare} />}
                {!active && <FontAwesomeIcon icon={uncheckedIcon ? uncheckedIcon : faSquare} />}
                <span className="ms-2">{children}</span>
            </span>
        </div>
    )
}

const CheckBoxField = ({ label, checked, onClick, className }) => {
  return (
    <span
      className={className + ' cursor-pointer'}
      onClick={onClick}
      style={{userSelect: 'none'}}
    >
      <FontAwesomeIcon icon={checked ? faCheckSquare : faSquare} className={label ? 'me-2' : ''} />
      <span>{label}</span>
    </span>
  )
}

const Modal = ({ title, type, size, description, show, buttons, onButtonClick, children, data }) => {

  const onClose = () => {
    onButtonClick('close', data)
  }

  const buttonsSet = [
        {key: 'close', label: 'Close', type: 'secondary'},
        {key: 'cancel', label: 'Cancel', type: 'light'},
        {key: 'delete', label: 'Delete', type: 'danger'},
        {key: 'ok', label: 'Ok', type: 'secondary'},
        {key: 'okay', label: 'Ok', type: 'primary'},
        {key: 'run', label: 'Run action', type: 'primary'},
        {key: 'no', label: 'No', type: 'secondary'},
        {key: 'yes', label: 'Yes', type: 'success'},
        {key: 'sign-out', label: 'Sign-Out', type: 'success'},
        {key: 'flag', label: 'Flag', type: 'danger'},
        {key: 'agree', label: 'I agree', type: 'success'},
        {key: 'send', label: 'Send', type: 'success'},
  ]

  return (
    <ReactModal
      size={size ? size : 'lg'}
      show={show}
      onHide={onClose}
    >
      <ReactModal.Header closeButton>
        <ReactModal.Title>{title}</ReactModal.Title>
      </ReactModal.Header>

      {description && <ReactModal.Body>{description}</ReactModal.Body>}

      {children}

      {buttons?.length > 0 &&
        <ReactModal.Footer>
          {buttonsSet.filter(button => buttons.includes(button.key)).map(button => {
            const isDisabled = data && data[button.key + '_disabled']
            return (
              <button
                key={button.key}
                type="button"
                disabled={isDisabled}
                className={'btn btn-' + ((type && button.type == 'primary') ? type : button.type)}
                onClick={() => onButtonClick(button.key, data)}
              >
                {isDisabled && <FontAwesomeIcon icon={faSpinner} spin className="me-2" />}
                {button.label}
              </button>
            )
          })}
        </ReactModal.Footer>
      }
    </ReactModal>
  )
}


export const ReportUserDialog = forwardRef(({ id }, ref) => {

  const [ options, setOptions ] = useState({})
  const [ loading, setLoading ] = useState(false)
  const { listServices } = useContext(AppContext)

  const [ show, setShow ] = useState(false)

  const [ message, setMessage ] = useState('')
  const [ errors, setErrors ] = useState()

  const answers = [
    'Looks like a scammer',
    'Items look like a scam',
    'Rude or inappropriate',
    'Problem with items or services',
  ]

  const addToast = useToastContext()

  useImperativeHandle(ref, () => ({
    modalDialog: _options => {
      return new Promise((resolve, reject) => {
        if(!_options) _options = {}
        _options.onButtonClick = button => {
          if(['cancel', 'close'].includes(button)){
            setShow(false)
            reject()
          }
          if(['confirm'].includes(button)){
            setShow(false)
            setMessage('')
            resolve()
          }
        }
        setShow(true)
        setOptions(_options)
      })
    }
  }))

  const report = () => {
    setLoading(true)
    listServices.toggle({
      type: 'flagged',
      listable_id: id,
      listable_type: 'App\\User',
      status: true,
      message: message
    }).then(res=> {
      setLoading(false)
      options.onButtonClick('confirm')
    }).catch(err => {
      setErrors(err?.data?.errors)
      addToast({ type: 'danger', title: 'Error', message: err?.data?.message ? err.data.message : 'Something went wrong' })
      setLoading(false)
    })
  }

  return (
    <Modal {...options} show={show}>
      <div className="card-body">
        <label>Can you share with us why you are flagging this user?</label>
        <div className="mb-3">
          {answers.map((item, i) =>
            <button
              key={i}
              onClick={() => setMessage(item)}
              className="btn btn-outline-primary me-2 my-1"
            >
              {item}
            </button>
          )}
        </div>
        <div className="mb-3">
          <textarea
              className="form-control"
              rows="5"
              placeholder="Enter your comments..."
              value={message}
              onChange={e => {
                setMessage(e.target.value)
                setErrors()
              }}
          ></textarea>
          {errors?.message && <div className="invalid-feedback d-block">{errors.message}</div>}
        </div>
        <div className="text-right">
          <button
            onClick={() => options.onButtonClick('close')}
            className="btn btn-light border ms-2"
          >Cancel</button>
          <button
            onClick={report}
            disabled={loading}
            className="btn btn-danger ms-2"
          >
            {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Report'}
          </button>
        </div>
      </div>
    </Modal>
  )
})

export { CheckBoxField, Checkbox, Modal }